// Empty, error and loading states, shared by every list and widget so "nothing yet", "could not
// load" and "loading" look the same everywhere.
import { WarningCircle } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { useT } from "../i18n";
import { cn } from "./cn";

export function EmptyState({
  icon,
  title,
  children,
  action,
  className,
  compact,
}: {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** Inside a card: no dashed frame, less padding. */
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-2 text-center",
        compact ? "px-4 py-8" : "rounded-xl border border-dashed border-gray-300 bg-gray-0/60 px-6 py-12 dark:bg-gray-50/60",
        className,
      )}
    >
      {icon ? (
        <span className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-500 [&>svg]:h-[22px] [&>svg]:w-[22px]">
          {icon}
        </span>
      ) : null}
      <div className="font-display text-[15px] font-semibold text-gray-900">{title}</div>
      {children ? <div className="max-w-md text-[13px] leading-relaxed text-gray-500">{children}</div> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ title, message, className }: { title?: string; message: string; className?: string }) {
  const t = useT();
  return (
    <div role="alert" className={cn("flex items-start gap-3 rounded-xl border border-red/40 bg-red-lighter/40 p-4", className)}>
      <WarningCircle weight="fill" className="mt-0.5 h-5 w-5 shrink-0 text-red" />
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold text-red-dark">{title ?? t.common.states.couldNotLoad}</div>
        <div className="break-words text-[13px] text-gray-600">{message}</div>
      </div>
    </div>
  );
}

export function Loading({ label, className }: { label?: string; className?: string }) {
  const t = useT();
  return (
    <div className={cn("flex items-center gap-2.5 py-6 text-[13px] text-gray-500", className)} role="status">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary" aria-hidden />
      {label ?? t.common.states.loading}
    </div>
  );
}
